
import { sha256 } from 'js-sha256';
import _ from 'lodash';
import moment from 'moment';

import { maybeRedirectToWelcomePage } from '../app/actions';
import logger from '../app/logger';
import { APP_WILL_MOUNT, APP_WILL_UNMOUNT } from '../base/app';
import { CONFERENCE_FAILED, CONFERENCE_JOINED } from '../base/conference';
import { apiKEY } from '../base/config/constants';
import { MHConferenceErrors, MHConferenceEvents } from '../base/lib-meet-hour';
import { fetchAxiosApi } from '../base/mtApi/functions';
import { getFirstLoadableAvatarUrl, getParticipantDisplayName, isLocalParticipantModerator } from '../base/participants';
import { USER_WAITING_REGISTER, USER_WAITING_SOUND } from '../base/premeeting/components/web/constants';
import { MiddlewareRegistry, StateListenerRegistry } from '../base/redux';
import { playSound,
    registerSound,
    stopSound,
    unregisterSound
} from '../base/sounds';
import { getDisabledSounds } from '../base/sounds/functions.any';
import { isTestModeEnabled } from '../base/testing';
import { isDomainWeborNative } from '../base/util/checkOS';
import { NOTIFICATION_TYPE, joinLeaveNotificationsDisabled, showNotification } from '../notifications';
import { shouldAutoKnock, shouldKnockBasedOnCriteria } from '../prejoin/functions';

import { HANDLE_SOUND, KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED, OPEN_LOBBY } from './actionTypes';
import {
    disableLobbySound,
    hideLobbyScreen,
    knockingParticipantLeft,
    openLobbyScreen,
    participantIsKnockingOrUpdated,
    reconcileLobbyFromMetadata,
    setLobbyModeEnabled,
    setPasswordJoinFailed,
    startKnocking,
    StartSound as startSound } from './actions';
import { INCOMING_USER_REQ_SOUND_ID } from './components/constants';
import { INCOMING_USER_KNOCKING_SOUND_FILE } from './components/sounds';

const LOBBY_SOUND_PREFERENCE_MESSAGE_TYPE = 'lobby-sound-preference';

/**
 * This is a work around as the component is in a
 * loop state which resets the sound functionality and hits the api constantly.
 */
export const confg = {
    runFetch: false,
    nativeAudio: false
};

/**
 * Resolves whether lobby knock sound should play using disabled sounds list as
 * one source and disabledLobbySound as another source.
 *
 * @param {Object} state - Redux state.
 * @param {Array<string>} disabledSounds - Disabled sound ids.
 * @returns {boolean}
 */
function _shouldPlayLobbyKnockSound(state, disabledSounds) {
    const { disabledLobbySound } = state['features/lobby'] || {};
    const hasSoundRegistry = Boolean(disabledSounds && typeof disabledSounds.includes === 'function');
    const disabledBySoundRegistry = hasSoundRegistry
        ? disabledSounds.includes(INCOMING_USER_REQ_SOUND_ID)
        : false;

    return !(disabledBySoundRegistry || Boolean(disabledLobbySound));
}

MiddlewareRegistry.register(store => next => action => {
    switch (action.type) {
    case APP_WILL_MOUNT: {
        store.dispatch(registerSound(USER_WAITING_REGISTER, USER_WAITING_SOUND, { loop: true }));
        break;
    }
    case APP_WILL_UNMOUNT: {
        store.dispatch(unregisterSound(USER_WAITING_REGISTER));
        break;
    }
    case CONFERENCE_FAILED:
        return _conferenceFailed(store, next, action);
    case CONFERENCE_JOINED:
        return _conferenceJoined(store, next, action);

    case OPEN_LOBBY:
    {
        const { dispatch, getState } = store;

        if (action.lobbyscreen._isLobbyScreenVisible) {
            if (!confg.isSound && navigator.product !== 'ReactNative') {
                confg.isSound = true;

                dispatch(startSound(true));
            }
            const { MH_CLIENT_ID } = getState()['features/base/config'];

            if (action.lobbyscreen?._isPrejoin && !confg.runFetch) {
                confg.runFetch = true;
                const moement = moment().seconds(0)
                .milliseconds(0)
                .format('X');

                const mesh256 = sha256(`${apiKEY}:MH:${moement}`);
                const fett = JSON.stringify({
                    client_id: MH_CLIENT_ID,
                    credentials: mesh256,
                    ...isDomainWeborNative,
                    'meeting_id': getState()['features/base/conference']?.room,
                    'is_owner': 1,
                    'participant_name': getState()['features/base/settings']?.displayName,
                    'participant_email': getState()['features/base/settings']?.email

                });


                if (navigator.product === 'ReactNative') {
                    const { API_URL_PREFIX, API_VERSION_PREFIX } = getState()['features/base/config'];

                    _.once(() => {
                        fetchAxiosApi('notify_waiting', {
                            method: 'post',
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            body: fett,
                            config: {
                                API_URL_PREFIX,
                                API_VERSION_PREFIX
                            }
                        })
                        .then(res => logger.debug('notify_waiting', res))
                        .catch(errs => logger.error('errs user APi', errs));
                    });
                } else {
                    fetchAxiosApi('notify_waiting', {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: fett
                    }).then(() => {
                        confg.runFetch = true;
                    })
                        .catch(errs => logger.error('errs APi', errs));
                }


            }

        }


        return next(action);
    }

    case HANDLE_SOUND: {
        const { dispatch } = store;
        const state = store.getState();
        let disabledSounds = [];

        getDisabledSounds(state)
        .then(options => {
            disabledSounds = options;

            if (action.soundBool) {
                if (!disabledSounds.includes('USER_WAITING_REGISTER')) {
                    dispatch(playSound(USER_WAITING_REGISTER));
                }
            } else if (!disabledSounds.includes('USER_WAITING_REGISTER')) {
                dispatch(stopSound(USER_WAITING_REGISTER));
            }
        });


        return next(action);
    }

    case KNOCKING_PARTICIPANT_ARRIVED_OR_UPDATED: {
        // We need the full update result to be in the store already
        const result = next(action);

        _findLoadableAvatarForKnockingParticipant(store, action.participant);

        return result;
    }
    }

    return next(action);
});

/**
 * Registers a change handler for state['features/base/conference'].conference to
 * set the event listeners needed for the lobby feature to operate.
 */
StateListenerRegistry.register(
    state => state['features/base/conference'].conference,
    (conference, { dispatch, getState }, previousConference) => {
        if (conference && !previousConference) {
            conference.on(MHConferenceEvents.MEMBERS_ONLY_CHANGED, enabled => {
                dispatch(setLobbyModeEnabled(enabled));
            });

            conference.on(MHConferenceEvents.LOBBY_USER_JOINED, (id, name) => {
                dispatch(participantIsKnockingOrUpdated({
                    id,
                    name
                }));
                const state = getState();
                let disabledSounds = [];

                getDisabledSounds(state)
                .then(options => {
                    disabledSounds = options;

                    if (_shouldPlayLobbyKnockSound(state, disabledSounds)) {
                        dispatch(playSound(INCOMING_USER_REQ_SOUND_ID));
                    }

                });
            });

            conference.on(MHConferenceEvents.LOBBY_USER_UPDATED, (id, participant) => {
                dispatch(participantIsKnockingOrUpdated({
                    ...participant,
                    id
                }));
            });

            conference.on(MHConferenceEvents.LOBBY_USER_LEFT, id => {
                dispatch(knockingParticipantLeft(id));
            });

            conference.on(MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED, (origin, sender) =>
                _maybeHandleLobbySoundPreference(origin, sender, {
                    dispatch,
                    getState
                }) || _maybeSendLobbyNotification(origin, sender, {
                    dispatch,
                    getState
                })
            );

            // The room-level lobby decision is stored in room metadata and
            // broadcast to every moderator (including late joiners). Reconcile
            // the actual members-only state to that decision so a moderator whose
            // JWT force-enabled the lobby does not override a prior "off" choice.
            conference.on(MHConferenceEvents.METADATA_UPDATED, () => {
                dispatch(reconcileLobbyFromMetadata());
            });
        }
    });

/**
 * Function to handle the conference failed event and navigate the user to the lobby screen
 * based on the failure reason.
 *
 * @param {Object} store - The Redux store.
 * @param {Function} next - The Redux next function.
 * @param {Object} action - The Redux action.
 * @returns {Object}
 */
function _conferenceFailed({ dispatch, getState }, next, action) {
    const { error } = action;
    const state = getState();
    const nonFirstFailure = Boolean(state['features/base/conference'].membersOnly);


    if (error.name === MHConferenceErrors.AUTHENTICATION_REQUIRED) {
        if (typeof error.recoverable === 'undefined') {
            error.recoverable = true;
        }

        const result = next(action);

        // dispatch(registerSound(USER_WAITING_REGISTER, USER_WAITING_SOUND, { loop: true }));


        // dispatch(openDialogs(PrejoinWaitng, {
        //     _isLobbyScreenVisible: true,
        //     _isPrejoin: true
        // }));
        // } else {
        dispatch(openLobbyScreen(true));


        // if (shouldAutoKnock(state)) {
        //     dispatch(startKnocking());
        // }
        // dispatch(setPasswordJoinFailed(nonFirstFailure));

        return result;

    }

    if (error.name === MHConferenceErrors.MEMBERS_ONLY_ERROR) {
        if (typeof error.recoverable === 'undefined') {
            error.recoverable = true;
        }

        const result = next(action);


        dispatch(openLobbyScreen(false));

        if (shouldAutoKnock(state) || shouldKnockBasedOnCriteria(state)) {
            dispatch(startKnocking());
        }

        dispatch(setPasswordJoinFailed(nonFirstFailure));

        return result;
    }


    if (error.name === MHConferenceErrors.CONFERENCE_ACCESS_DENIED) {
        dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.ERROR,
            hideErrorSupportLink: true,
            titleKey: 'lobby.joinRejectedMessage'
        }));
        setTimeout(dispatch(maybeRedirectToWelcomePage()), 5000);
    }

    return next(action);
}

/**
 * Applies a lobby sound preference update shared by another moderator.
 *
 * @param {Object} origin - The origin (initiator) of the message.
 * @param {Object} message - The actual message.
 * @param {Object} store - The Redux store.
 * @returns {boolean} True when message is handled.
 */
function _maybeHandleLobbySoundPreference(origin, message, { dispatch, getState }) {
    if (!origin?._id || message?.type !== LOBBY_SOUND_PREFERENCE_MESSAGE_TYPE) {
        return false;
    }

    if (!isLocalParticipantModerator(getState()) || typeof message.disabledLobbySound !== 'boolean') {
        return true;
    }

    dispatch(disableLobbySound(message.disabledLobbySound, { broadcast: false }));

    return true;
}

/**
 * Handles cleanup of lobby state when a conference is joined.
 *
 * @param {Object} store - The Redux store.
 * @param {Function} next - The Redux next function.
 * @param {Object} action - The Redux action.
 * @returns {Object}
 */
function _conferenceJoined({ dispatch }, next, action) {
    dispatch(hideLobbyScreen());
    dispatch(registerSound(INCOMING_USER_REQ_SOUND_ID, INCOMING_USER_KNOCKING_SOUND_FILE));
    dispatch(unregisterSound(USER_WAITING_REGISTER));

    return next(action);
}

/**
 * Finds the loadable avatar URL and updates the participant accordingly.
 *
 * @param {Object} store - The Redux store.
 * @param {Object} participant - The knocking participant.
 * @returns {void}
 */
function _findLoadableAvatarForKnockingParticipant(store, { id }) {
    const { dispatch, getState } = store;
    const updatedParticipant = getState()['features/lobby'].knockingParticipants.find(p => p.id === id);
    const { disableThirdPartyRequests } = getState()['features/base/config'];

    if (!disableThirdPartyRequests && updatedParticipant && !updatedParticipant.loadableAvatarUrl) {
        getFirstLoadableAvatarUrl(updatedParticipant, store).then(loadableAvatarUrl => {
            if (loadableAvatarUrl) {
                dispatch(participantIsKnockingOrUpdated({
                    loadableAvatarUrl,
                    id
                }));
            }
        });
    }
}

/**
 * Check the endpoint message that arrived through the conference and
 * sends a lobby notification, if the message belongs to the feature.
 *
 * @param {Object} origin - The origin (initiator) of the message.
 * @param {Object} message - The actual message.
 * @param {Object} store - The Redux store.
 * @returns {void}
 */
function _maybeSendLobbyNotification(origin, message, { dispatch, getState }) {
    if (!origin?._id || message?.type !== 'lobby-notify') {
        return;
    }

    const notificationProps: any = {
        descriptionArguments: {
            originParticipantName: getParticipantDisplayName(getState, origin._id),
            targetParticipantName: message.name
        },
        titleKey: 'lobby.notificationTitle'
    };

    switch (message.event) {
    case 'LOBBY-ENABLED':
        notificationProps.descriptionKey = `lobby.notificationLobby${message.value ? 'En' : 'Dis'}abled`;
        break;
    case 'LOBBY-ACCESS-GRANTED':
        notificationProps.descriptionKey = 'lobby.notificationLobbyAccessGranted';
        break;
    case 'LOBBY-ACCESS-DENIED':
        notificationProps.descriptionKey = 'lobby.notificationLobbyAccessDenied';
        break;
    }

    if (joinLeaveNotificationsDisabled(getState()) === false) {
        dispatch(showNotification(notificationProps, isTestModeEnabled(getState()) ? undefined : 5000));

    }
}
